/**
 * CareersForm — public-facing "Send us your resume" form.
 * Posts to POST /api/forms/careers. Resume is uploaded as base64 (≤2 MB).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Upload, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESUME_BYTES = 2 * 1024 * 1024; // 2 MB

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const CareersForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', cover_note: '' });
  const [resume, setResume] = useState(null); // { name, base64 }
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_RESUME_BYTES) {
      toast.error('Resume too large — keep it under 2 MB');
      return;
    }
    try {
      const b64 = await fileToBase64(f);
      setResume({ name: f.name, base64: b64 });
    } catch { toast.error('Could not read file'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/forms/careers`, {
        ...form,
        resume_base64: resume?.base64 || '',
        resume_filename: resume?.name || '',
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch within 5 business days.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="rounded-3xl border-2 border-[#FF0059] bg-[#FFE5EE]/40 p-8 text-center" data-testid="careers-form-success">
        <CheckCircle2 className="h-12 w-12 text-[#FF0059] mx-auto mb-3" />
        <p className="text-2xl font-black text-black mb-1">Application received</p>
        <p className="text-gray-600">We'll reach out to {form.email} within 5 business days.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border-2 border-gray-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="careers-form">
      <h3 className="text-2xl font-black text-black mb-1">Send us your resume</h3>
      <p className="text-sm text-gray-600 mb-5">We hire across engineering, operations, hospitality &amp; growth. Drop your details and we'll reach out.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="careers-name" />
        <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="careers-email" />
        <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="careers-phone" />
        <Input label="Role you're applying for" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testId="careers-role" placeholder="e.g. Backend Engineer" />
      </div>
      <div className="mt-3">
        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Cover note (optional)</label>
        <textarea
          rows={3}
          value={form.cover_note}
          onChange={(e) => setForm({ ...form, cover_note: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm resize-none"
          placeholder="What excites you about localliink.com?"
          data-testid="careers-cover-note"
        />
      </div>
      <div className="mt-3">
        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Resume (PDF/DOC · 2 MB max)</label>
        <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-200 cursor-pointer hover:border-[#FF0059] transition-colors" data-testid="careers-resume-pick">
          <Upload className="h-4 w-4 text-[#FF0059]" />
          <span className="text-sm font-bold text-black truncate">{resume?.name || 'Choose file…'}</span>
          <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={onFile} className="hidden" data-testid="careers-resume-input" />
        </label>
      </div>
      <Button type="submit" disabled={submitting} className="mt-5 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="careers-submit">
        <Send className="h-4 w-4 mr-2" /> {submitting ? 'Sending…' : 'Send my application'}
      </Button>
    </form>
  );
};

const Input = ({ label, value, onChange, type = 'text', placeholder, testId }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm"
      data-testid={testId}
    />
  </div>
);
